import React, { ReactNode } from 'react';
import { useNode } from '@craftjs/core';
import { ATRIBUTO_AREA_LIBRE, useAltoMovilDeHijosLibres } from '../utils/posicionLibre';

type Alineacion = 'stretch' | 'start' | 'center' | 'end';
/**
 * Modo de layout de la sección.
 *
 * `flow` es el de siempre: los hijos se apilan uno abajo del otro. `free`
 * habilita que cada hijo se pueda posicionar libremente dentro del área de
 * contenido.
 *
 * El default es `flow` y el cambio NO migra nada: una sección que pasa a `free`
 * se ve exactamente igual hasta que alguien arrastre un elemento, porque el
 * posicionamiento se activa por hijo cuando ese hijo tiene su prop `_layout`.
 */
type ModoLayout = 'flow' | 'free';

interface SectionProps {
  children?: ReactNode;
  background?: string;
  minHeight?: number;
  /**
   * Alto mínimo ABAJO del corte. Ausente = la sección se mide por su contenido.
   * Ver `PROP_ALTO_SECCION_MOVIL`. MANTENER EN SYNC con goautos-admin.
   */
  minHeightMovil?: number;
  paddingY?: number;
  paddingX?: number;
  fullWidth?: boolean;
  maxWidth?: number;
  contentAlign?: Alineacion;
  layoutMode?: ModoLayout;
}

const ALINEAR: Record<Alineacion, string> = {
  stretch: 'stretch',
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

/**
 * Sección en blanco — render del sitio público.
 *
 * Una franja de ancho completo donde el dealer mete lo que quiera. A diferencia
 * de `Container`, que es un elemento DENTRO de una sección, ésta se comporta
 * como una sección de primer nivel: fondo a sangre, ancho de contenido acotado
 * por `maxWidth` y padding vertical propio.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/Section.tsx.
 * La única diferencia es que allá se dibuja el estado vacío para poder soltarle
 * cosas adentro; acá una sección sin hijos es simplemente una franja vacía, que
 * es lo que el dealer pidió al crearla.
 *
 * ## El marco de referencia en modo libre es la SECCIÓN ENTERA
 *
 * Un hijo posicionado es `absolute`, así que su bloque contenedor es el padding
 * box del ancestro posicionado más cercano — la sección. Cuando el atributo
 * vivía en la caja de contenido, esa caja quedaba adentro del padding y las
 * cuentas del gesto usaban un origen que no era el que usaba el navegador para
 * pintar: `y = 0` caía 48px más abajo del borde real y el elemento nunca llegaba
 * hasta arriba. Poniéndolo acá, el origen del cálculo y el del pintado son el
 * mismo punto, y es además el mismo criterio que la capa de una sección
 * existente (`inset: 0`).
 *
 * La caja de contenido sigue igual y en el flujo: los hijos que todavía no se
 * movieron no se enteran de nada, que es lo que hace que pasar una sección a
 * libre no mueva un píxel.
 */
/**
 * El alto mínimo de una sección vale de `md` para arriba, no en el teléfono.
 *
 * Es un alto que alguien eligió mirando una pantalla ancha. Aplicado tal cual
 * en 390px reserva un espacio que el contenido ya no necesita: en móvil los
 * elementos libres vuelven al flujo y ocupan mucho menos que su caja de
 * escritorio, así que la sección quedaba con cientos de píxeles en blanco
 * abajo del contenido y empujaba todo lo que venía después.
 *
 * Medido en VDT a 390px con el carrusel ya arreglado: el banner terminaba a
 * los 260px y la sección seguía hasta los 629 — 369px de nada.
 *
 * Abajo del corte la sección se mide por lo que tiene adentro, que es el
 * comportamiento por defecto de cualquier bloque. No hay un alto mínimo
 * propio de móvil en el modelo: si alguna vez lo hay, entra acá.
 *
 * Las clases van completas y las medidas en variables CSS porque Tailwind
 * purga por coincidencia de texto y no ve lo que se arma interpolando. Y van
 * en CLASE y no en el `style` porque un estilo en línea le gana a cualquier
 * clase: ahí adentro el `md:` no se aplicaría nunca.
 */
/**
 * El padding vertical de la sección cede en pantallas angostas.
 *
 * `paddingY` se elige mirando el canvas de 1280: 48px arriba y 48 abajo es un
 * respiro ahí y un agujero en un teléfono. Medido en la home de B2 Autos a
 * 390px: el banner mide 183px y estaba rodeado de 96px de padding — entre el
 * banner y el título siguiente quedaban 96px de blanco, más de la mitad del
 * alto del propio banner.
 *
 * `min(48px, 6.25vw)` es el valor guardado mientras haya lugar —desde 768px de
 * viewport en adelante no cambia nada— y de ahí para abajo cede de forma
 * continua: 37px a 600, 24px a 390, 20px a 320. Sin breakpoint y sin poder
 * agrandar nunca, así que el escritorio queda intacto.
 *
 * Un padding chico no se entera: `min(8px, 6.25vw)` sólo mordería abajo de
 * 128px de ancho.
 */
const padYFluido = (px: number): string => `min(${px}px, 6.25vw)`;

const MIN_H_SECCION = 'md:min-h-[var(--seccion-min-h)]';

/**
 * En una sección LIBRE el mismo alto mínimo arranca recién en `xl`.
 *
 * El corte de `md` se escribió cuando el posicionamiento libre volvía al flujo
 * en 768px. Desde que el corte de `posicionLibre` es `xl` (1280), entre 768 y
 * 1280 pasaba lo peor de los dos mundos: los hijos ya están apilados en el
 * flujo y ocupan mucho menos que su caja de escritorio, pero la sección seguía
 * reservando un alto elegido mirando el canvas de 1280.
 *
 * Medido en la home de B2 Autos a 790px: el banner termina en 543 y la sección
 * seguía hasta 751 — 208px de blanco, de los que sólo 48 son el padding.
 *
 * Sólo para `free`: en una sección de flujo los hijos nunca se movieron de su
 * sitio, el mínimo es decorativo (una franja con fondo) y sacarlo entre 768 y
 * 1280 sí cambiaría páginas que hoy están bien.
 */
const MIN_H_SECCION_LIBRE = 'lg:min-h-[var(--seccion-min-h)]';

/** Ídem para la caja de contenido, cuyo mínimo se deriva del de la sección. */
const MIN_H_CONTENIDO = 'md:min-h-[var(--seccion-min-h-int)]';

/** Ídem para la caja de contenido de una sección libre. */
const MIN_H_CONTENIDO_LIBRE = 'lg:min-h-[var(--seccion-min-h-int)]';

/**
 * Abajo de `xl` la caja de contenido de una sección libre es una FILA QUE
 * ENVUELVE, no una pila.
 *
 * Ahí los hijos vuelven al flujo, y apilados de a uno a todo el ancho una tira
 * de cuatro logos ocupaba media pantalla con cada pieza tres veces más grande
 * que su original. El sitio de referencia los reparte en filas de dos. Cuánto
 * pide cada hijo lo decide él mismo — ver `BASE_FILA_MARCO` en `posicionLibre`.
 *
 * `lg:block` devuelve el bloque de siempre arriba del corte, donde los hijos son
 * absolutos y esto no pinta nada. Y una sección con UN solo hijo se ve igual que
 * antes: una fila de un elemento es el propio elemento.
 */
const FILA_LIBRE = 'flex flex-wrap items-center justify-center gap-3 lg:block';

/**
 * El alto que reserva una sección cuya composición MÓVIL ya está congelada.
 *
 * Abajo de `lg` las piezas vuelven al flujo y lo empujan ellas, así que una
 * sección sin composición móvil no necesita ningún mínimo acá —y por eso la
 * variable sólo se emite cuando hay algo que reservar: sin ella la clase no
 * resuelve y la sección se sigue midiendo como siempre—. Pero en cuanto el
 * dealer mueve una pieza en móvil, el Builder pasa la sección entera a
 * `absolute` y ya no queda nadie en el flujo: sin este mínimo la sección se
 * serviría colapsada a su padding, con las piezas dibujadas afuera.
 *
 * Se corta en `lg` porque de ahí para arriba manda `MIN_H_SECCION_LIBRE`, que
 * es el alto de escritorio que el dealer eligió con la manija.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
const MIN_H_SECCION_MOVIL = 'max-lg:min-h-[var(--seccion-min-movil)]';

export const Section = ({
  children,
  background = 'transparent',
  minHeight = 240,
  minHeightMovil = 0,
  paddingY = 48,
  paddingX = 16,
  fullWidth = true,
  maxWidth = 1280,
  contentAlign = 'stretch',
  layoutMode = 'flow',
}: SectionProps) => {
  const { connectors, id } = useNode();

  const libre = layoutMode === 'free';
  // Hasta dónde llega la pieza más baja de la composición MÓVIL. Cero mientras
  // nadie haya compuesto en móvil, que es cuando las piezas siguen en el flujo.
  const altoMovilDeLosHijos = useAltoMovilDeHijosLibres(id, libre);

  const alto = Math.max(0, Number(minHeight) || 0);
  /** El alto que el dealer eligio para la vista responsive. Cero = no hay. */
  const altoMovil = Math.max(0, Number(minHeightMovil) || 0);
  const anchoMax = Math.max(0, Number(maxWidth) || 0);

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      {...(libre ? { [ATRIBUTO_AREA_LIBRE]: '' } : {})}
      // El alto mínimo vale de `md` para arriba. Abajo la sección se mide por
      // lo que tiene adentro: ver `MIN_H_SECCION`.
      className={[
        alto ? (libre ? MIN_H_SECCION_LIBRE : MIN_H_SECCION) : '',
        altoMovilDeLosHijos || altoMovil ? MIN_H_SECCION_MOVIL : '',
      ].filter(Boolean).join(' ') || undefined}
      style={{
        width: '100%',
        background,
        paddingTop: padYFluido(paddingY),
        paddingBottom: padYFluido(paddingY),
        paddingLeft: `${paddingX}px`,
        paddingRight: `${paddingX}px`,
        // Lo lee el banner para salirse a sangre completa en el teléfono.
        ['--seccion-px' as string]: `${paddingX}px`,
        ...(alto ? { ['--seccion-min-h' as string]: `${alto}px` } : {}),
        // El alto efectivo abajo del corte es el MÁXIMO entre lo que el dealer
        // eligió con la manija y lo que necesitan las piezas ya compuestas en
        // móvil. `max()` de CSS resuelve las dos cosas de una: achicar nunca
        // corta ni superpone, porque el término del contenido pone el piso;
        // agrandar sí deja aire, porque ahí gana el elegido.
        //
        // El fondo de la pieza más baja se mide desde el borde de arriba de la
        // sección —el padding ya está adentro—, así que sólo falta sumarle el de
        // abajo para que no quede pegada al borde. Ver `MIN_H_SECCION_MOVIL`.
        ...(altoMovilDeLosHijos || altoMovil
          ? {
            ['--seccion-min-movil' as string]: altoMovilDeLosHijos && altoMovil
              ? `max(${altoMovil}px, calc(${altoMovilDeLosHijos}px + ${padYFluido(paddingY)}))`
              : (altoMovil
                ? `${altoMovil}px`
                : `calc(${altoMovilDeLosHijos}px + ${padYFluido(paddingY)})`),
          }
          : {}),
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      <div
        // Su `min-height` sale del alto de la SECCIÓN, no de lo que tiene
        // adentro. Queda marcado porque el editor mide "cuánto necesita el
        // contenido" para saber hasta dónde se puede achicar la sección, y esta
        // caja le devolvía el alto que la sección ya tenía: un mínimo que crecía
        // solo con la sección y no la dejaba volver a achicarse nunca.
        data-alto-derivado=""
        // Mismo corte que la sección: su mínimo también arranca en `md`.
        className={[
          alto ? (libre ? MIN_H_CONTENIDO_LIBRE : MIN_H_CONTENIDO) : '',
          libre ? FILA_LIBRE : '',
        ].filter(Boolean).join(' ') || undefined}
        style={{
          width: '100%',
          ...(fullWidth || anchoMax === 0 ? {} : { maxWidth: `${anchoMax}px`, margin: '0 auto' }),
          ...(alto
            ? { ['--seccion-min-h-int' as string]: `${Math.max(0, alto - paddingY * 2)}px` }
            : {}),
          // En modo libre la caja de contenido queda SIN posicionar: así los
          // hijos absolutos se resuelven contra la sección y no contra ella.
          // La alineación del contenido tampoco aplica: cada hijo manda sobre
          // sí mismo.
          ...(libre || contentAlign === 'stretch'
            ? {}
            : { display: 'flex', flexDirection: 'column' as const, alignItems: ALINEAR[contentAlign] }),
        }}
      >
        {children}
      </div>
    </div>
  );
};

Section.craft = {
  displayName: 'Section',
  props: {
    background: 'transparent',
    minHeight: 240,
    paddingY: 48,
    paddingX: 16,
    fullWidth: true,
    maxWidth: 1280,
    contentAlign: 'stretch',
    layoutMode: 'flow',
  },
  rules: { canDrop: () => true },
};
